import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  Building2,
  ChevronDown,
  RefreshCw,
  Store,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import config from "../../../../config/Global.json";
import { useToastHandler } from "../../../common/ToastHandler";
import "./BusinessOverview.css";

const toLocalDateStr = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayStr = () => toLocalDateStr(new Date());

// Monday-start week helper: returns the Date for Monday of the week that
// `d` falls in.
const startOfWeek = (d) => {
  const copy = new Date(d);
  const day = copy.getDay(); // 0 = Sunday ... 6 = Saturday
  const diffToMonday = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diffToMonday);
  return copy;
};

const PRESETS = [
  {
    key: "this_month",
    label: "This Month",
    range: () => {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: now,
      };
    },
  },
  {
    key: "last_month",
    label: "Last Month",
    range: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start, end };
    },
  },
  {
    key: "this_week",
    label: "This Week",
    range: () => {
      const now = new Date();
      return { start: startOfWeek(now), end: now };
    },
  },
  {
    key: "last_week",
    label: "Last Week",
    range: () => {
      const now = new Date();
      const thisMonday = startOfWeek(now);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(lastSunday.getDate() - 1);
      return { start: lastMonday, end: lastSunday };
    },
  },
];

const BusinessOverview = () => {
  const { Get, Post, getAPI } = useApi();
  const { formatCurrency } = useFormattingUtils();
  const { setManualAuthToken } = useContext(AuthContext);
  const { handleToast } = useToastHandler();

  const thisMonth = PRESETS[0].range();
  const [startDate, setStartDate] = useState(toLocalDateStr(thisMonth.start));
  const [endDate, setEndDate] = useState(toLocalDateStr(thisMonth.end));
  const [activePreset, setActivePreset] = useState("this_month");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overall, setOverall] = useState(null);
  const [stores, setStores] = useState([]);
  const [switchingStoreId, setSwitchingStoreId] = useState(null);
  const [expandedStoreId, setExpandedStoreId] = useState(null);

  const fetchSummary = async (from = startDate, to = endDate) => {
    try {
      setLoading(true);
      setError("");
      const response = await Get(
        `${getAPI("ownerBusinessSummary")}?start_date=${from}&end_date=${to}`
      );
      setOverall(response?.overall || null);
      setStores(Array.isArray(response?.stores) ? response.stores : []);
      if (Array.isArray(response?.warnings) && response.warnings.length > 0) {
        console.warn("Business overview: some data sources failed to respond:", response.warnings);
      }
    } catch (fetchError) {
      const message =
        fetchError?.response?.data?.error ||
        fetchError?.message ||
        "Failed to load business overview.";
      setError(message);
      setOverall(null);
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const { start, end } = preset.range();
    const from = toLocalDateStr(start);
    const to = toLocalDateStr(end);
    setStartDate(from);
    setEndDate(to);
    setActivePreset(preset.key);
    fetchSummary(from, to);
  };

  const handleCustomDateChange = (which, value) => {
    setActivePreset(null);
    if (which === "start") setStartDate(value);
    else setEndDate(value);
  };

  const handleViewStoreReport = async (storeId) => {
    try {
      setSwitchingStoreId(storeId);
      const selectResponse = await Post("selectStore", { store_id: storeId });

      const currentAuthToken = JSON.parse(localStorage.getItem("authToken"));
      const refreshToken =
        localStorage.getItem("refresh_token") || currentAuthToken?.refresh;

      if (refreshToken) {
        try {
          const apiUrl = `${config.api.host}${config.api.refreshToken}`;
          const tokenResponse = await axios.post(
            apiUrl,
            { refresh: refreshToken },
            { headers: { "Content-Type": "application/json" } }
          );
          if (tokenResponse.data && tokenResponse.data.access) {
            setManualAuthToken(tokenResponse.data);
          }
        } catch (tokenError) {
          console.error("Token refresh failed:", tokenError);
        }
      }

      handleToast({
        type: "SUCCESS",
        message: `Switched to ${selectResponse?.data?.store_name || "selected store"}.`,
      });

      // Full reload so every part of the app (header, permissions, store
      // context) picks up the newly-selected store cleanly.
      window.location.href = "/Report";
    } catch (switchError) {
      handleToast({
        type: "ERROR",
        message:
          switchError?.response?.data?.error ||
          "Failed to switch to that store. Please try again.",
      });
      setSwitchingStoreId(null);
    }
  };

  const toggleExpanded = (storeId) => {
    setExpandedStoreId((current) => (current === storeId ? null : storeId));
  };

  const rangeLabel = useMemo(() => {
    if (activePreset) {
      return PRESETS.find((p) => p.key === activePreset)?.label || "";
    }
    return `${startDate} to ${endDate}`;
  }, [activePreset, startDate, endDate]);

  return (
    <div className="responsive-box">
      <div className="bo-page">
      <div className="bo-header">
        <div>
          <h1 className="bo-title">Business Overview</h1>
          <p className="bo-subtitle">All your stores, at a glance &middot; {rangeLabel}</p>
        </div>

        <button className="bo-refresh-btn" onClick={() => fetchSummary()} disabled={loading}>
          <RefreshCw size={16} className={loading ? "bo-spin" : ""} />
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="bo-filter-bar">
        <div className="bo-preset-group">
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              className={`bo-preset-btn ${activePreset === preset.key ? "active" : ""}`}
              onClick={() => applyPreset(preset)}
              disabled={loading}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="bo-date-controls">
          <label>
            From
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => handleCustomDateChange("start", e.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayStr()}
              onChange={(e) => handleCustomDateChange("end", e.target.value)}
            />
          </label>
          {!activePreset && (
            <button className="bo-apply-btn" onClick={() => fetchSummary()} disabled={loading}>
              Apply
            </button>
          )}
        </div>
      </div>

      {error && <div className="bo-error-banner">{error}</div>}

      {!error && (
        <>
          <div className="bo-summary-cards">
            <div className="bo-summary-card bo-card-sales">
              <div className="bo-summary-icon"><TrendingUp size={20} /></div>
              <div>
                <div className="bo-summary-label">Total Sales (all stores)</div>
                <div className="bo-summary-value">
                  {overall ? formatCurrency(overall.total_sales) : "—"}
                </div>
              </div>
            </div>
            <div className="bo-summary-card bo-card-expense">
              <div className="bo-summary-icon"><TrendingDown size={20} /></div>
              <div>
                <div className="bo-summary-label">Total Expenses (all stores)</div>
                <div className="bo-summary-value">
                  {overall ? formatCurrency(overall.total_expense) : "—"}
                </div>
              </div>
            </div>
            <div className="bo-summary-card bo-card-net">
              <div className="bo-summary-icon"><Building2 size={20} /></div>
              <div>
                <div className="bo-summary-label">Net</div>
                <div className="bo-summary-value">
                  {overall ? formatCurrency(overall.net) : "—"}
                </div>
              </div>
            </div>
            <div className="bo-summary-card bo-card-count">
              <div className="bo-summary-icon"><Store size={20} /></div>
              <div>
                <div className="bo-summary-label">Stores</div>
                <div className="bo-summary-value">{overall ? overall.store_count : "—"}</div>
              </div>
            </div>
          </div>

          {loading && stores.length === 0 && (
            <div className="bo-empty-state">Loading your stores...</div>
          )}

          {!loading && stores.length === 0 && (
            <div className="bo-empty-state">No store data found for this date range.</div>
          )}

          <div className="bo-store-cards">
            {stores.map((store) => {
              const isExpanded = expandedStoreId === store.store_id;
              return (
                <div className={`bo-store-card ${isExpanded ? "expanded" : ""}`} key={store.store_id}>
                  <button
                    className="bo-store-card-summary"
                    onClick={() => toggleExpanded(store.store_id)}
                    aria-expanded={isExpanded}
                  >
                    <ChevronDown size={18} className="bo-chevron" />
                    <span className="bo-store-name">{store.store_name}</span>
                    <span className="bo-store-metric">
                      <span className="bo-metric-label">Sales</span>
                      <span className="bo-store-sales">{formatCurrency(store.total_sales)}</span>
                    </span>
                    <span className="bo-store-metric">
                      <span className="bo-metric-label">Expenses</span>
                      <span className="bo-store-expense">{formatCurrency(store.total_expense)}</span>
                    </span>
                    <span className="bo-store-metric">
                      <span className="bo-metric-label">Net</span>
                      <span className={`bo-store-net ${store.net < 0 ? "negative" : ""}`}>
                        {formatCurrency(store.net)}
                      </span>
                    </span>
                  </button>

                  <div className="bo-store-card-actions">
                    <button
                      className="bo-view-report-btn"
                      onClick={() => handleViewStoreReport(store.store_id)}
                      disabled={switchingStoreId === store.store_id}
                    >
                      {switchingStoreId === store.store_id ? "Opening..." : "View Detailed Report"}
                      <ArrowRight size={14} />
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="bo-store-breakdown">
                      <div className="bo-breakdown-col">
                        <h4>Sales by app</h4>
                        {store.sales_breakdown && store.sales_breakdown.length > 0 ? (
                          <ul>
                            {store.sales_breakdown.map((row) => (
                              <li key={row.label}>
                                <span>{row.label}</span>
                                <span className="bo-breakdown-amount sales">
                                  {formatCurrency(row.amount)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="bo-breakdown-empty">No sales in this range.</p>
                        )}
                      </div>
                      <div className="bo-breakdown-col">
                        <h4>Expenses by app</h4>
                        {store.expense_breakdown && store.expense_breakdown.length > 0 ? (
                          <ul>
                            {store.expense_breakdown.map((row) => (
                              <li key={row.label}>
                                <span>{row.label}</span>
                                <span className="bo-breakdown-amount expense">
                                  {formatCurrency(row.amount)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="bo-breakdown-empty">No expenses in this range.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default BusinessOverview;
